import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Container, Stack, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import EventTopBar from '../components/eventDetails/EventTopBar';
import { paymentStatusStyles, topBarStyles } from '../components/eventDetails/eventDetailsStyles';
import { downloadPaymentReceipt, downloadPaymentTicket } from '../services/api';

const downloadTypeConfig = {
  ticket: {
    title: 'Downloading Your Ticket',
    description: 'Your ticket download should start automatically.',
    getFile: downloadPaymentTicket,
    fallbackName: 'ticket.pdf',
  },
  receipt: {
    title: 'Downloading Your Receipt',
    description: 'Your receipt download should start automatically.',
    getFile: downloadPaymentReceipt,
    fallbackName: 'receipt.pdf',
  },
};

const getFilenameFromDisposition = (disposition, fallbackName) => {
  if (!disposition) return fallbackName;

  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1] || fallbackName;
};

const DownloadPage = () => {
  const { transactionRef } = useParams();
  const [searchParams] = useSearchParams();
  const [downloadState, setDownloadState] = useState({ loading: true, error: '' });
  const type = searchParams.get('type') || 'ticket';
  const config = downloadTypeConfig[type] || downloadTypeConfig.ticket;
  const triggered = useRef(false);

  const hasTransactionRef = Boolean(transactionRef);

  const buttonLabel = useMemo(
    () => `Download ${type === 'receipt' ? 'Receipt' : 'Ticket'}`,
    [type],
  );

  const startDownload = useCallback(async () => {
    if (!transactionRef) {
      setDownloadState({ loading: false, error: 'Missing transaction reference.' });
      return;
    }

    setDownloadState({ loading: true, error: '' });

    try {
      const response = await config.getFile(transactionRef);
      const contentDisposition = response.headers?.['content-disposition'];
      const filename = getFilenameFromDisposition(contentDisposition, config.fallbackName);
      const blob = new Blob([response.data], { type: response.data?.type || 'application/octet-stream' });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
      setDownloadState({ loading: false, error: '' });
    } catch (error) {
      const message = error.response?.data?.detail || error.response?.data?.message || error.message || 'Unable to download file.';
      setDownloadState({ loading: false, error: message });
    }
  }, [config, transactionRef]);

  useEffect(() => {
    if (!hasTransactionRef || triggered.current) return;
    triggered.current = true;
    startDownload();
  }, [hasTransactionRef, startDownload]);

  return (
    <Box sx={paymentStatusStyles.page}>
      <Box sx={topBarStyles.shell}>
        <EventTopBar />
      </Box>

      <Container maxWidth="sm" sx={paymentStatusStyles.container}>
        <Card sx={paymentStatusStyles.card}>
          <CardContent sx={paymentStatusStyles.cardContent}>
            <Stack spacing={3} sx={{ alignItems: 'center', textAlign: 'center' }}>
              {downloadState.loading ? <CircularProgress size={48} sx={{ color: '#008578' }} /> : <DownloadIcon sx={{ fontSize: 48, color: '#008578' }} />}

              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{config.title}</Typography>
                <Typography color="text.secondary">{config.description}</Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  If the download does not start automatically, click the button below.
                </Typography>
              </Box>

              {downloadState.error && (
                <Alert severity="error" sx={{ width: '100%' }}>{downloadState.error}</Alert>
              )}

              {hasTransactionRef && (
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  sx={{ bgcolor: '#008578', '&:hover': { bgcolor: '#006b5f' }, px: 4 }}
                  onClick={startDownload}
                  disabled={downloadState.loading}
                >
                  {downloadState.loading ? 'Starting Download...' : buttonLabel}
                </Button>
              )}

              
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default DownloadPage;
